import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTablePresets } from '@/entities/settings';
import { useAppNavigate } from '@/shared/lib';
import { useDialogsSection } from './useDialogsSection';

/**
 * Держит согласованными раздел, открытый адресом, и вид списка в нём.
 *
 * Раздел закреплённого пресета ведёт свой вид: заход показывает то, что пользователь в нём
 * оставил, — включая правки, не сохранённые в пресет. Из самого пресета вид берётся только на
 * первом заходе, пока в разделе не выбран ни один пресет: раздел «Диалоги» ничего у соседей не
 * перенимает и наоборот, поэтому переход между разделами вид ни одного из них не переписывает.
 * Адрес, которому не отвечает ни один закреплённый пресет (открепление, удаление, переименование,
 * ссылка в никуда), уводит в раздел «Диалоги» — но только когда набор пресетов известен целиком:
 * пока он не получен, адрес раздела несуществующим не считается. Открытая адресом запись при этом
 * переносится в «Диалоги» вместе с условиями её открытия: запись доступна независимо от пресета,
 * и адрес, скопированный из раздела пресета, не должен терять диалог у того, кому этот пресет
 * не закреплён.
 *
 * Живёт на уровне страницы, а не списка: в раскладках, где список рядом с открытой записью не
 * показан, связь адреса и раздела должна работать так же.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`); `null` — раздел без пресетов.
 */
export const usePresetSectionSync = (tableName: string | null) => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id?: string }>();
  const { sectionKey, sectionRoute, isUnknownSection } = useDialogsSection(tableName ?? '');
  const { isPresetListKnown, activePresetId, apply } = useTablePresets(sectionKey);

  useEffect(() => {
    // Раздел без пресетов адрес с ними не связывает — своего раздела у пресета там нет.
    if (!tableName) return;
    // Пока набор пресетов не известен целиком, «пресета нет» отличить от «пресет ещё не пришёл»
    // нельзя: системные пресеты приходят отдельно и позже личных, и раздел системного пресета
    // терялся бы на каждой перезагрузке и при заходе по прямой ссылке.
    if (!isPresetListKnown) return;
    if (isUnknownSection) {
      // Условия открытия карточки живут в query адреса, поэтому с записью переносятся и они;
      // без записи в адресе переносить нечего — раздел показывает свой список.
      navigate('/dialogs/:id?', { id: id ?? null }, { replace: true, saveParams: Boolean(id) });
      return;
    }
    if (!sectionRoute) return;
    // Раздел без выбранного пресета — либо он открыт впервые, либо заведён прежними версиями
    // настроек: показываем вид самого пресета. Дальше вид раздела ведёт пользователь.
    if (activePresetId === undefined) {
      apply(sectionRoute.preset.id);
    }
  }, [tableName, id, sectionRoute, isUnknownSection, isPresetListKnown, activePresetId, apply, navigate]);
};
